'use client';

import { type ReactNode, useRef, useState } from 'react';

import { ChevronRight, LayoutGrid, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
} from '@kit/ui/drawer';
import { cn } from '@kit/ui/utils';

import {
  type SportVisual,
  resolveSportVisual,
} from '~/(tenant)/_components/native/_lib/sport-icons';
import { SportVisualView } from '~/(tenant)/_components/native/_lib/sport-visual-view';

import { BookingChipIcon } from './booking-chip-icon';
import { useBookingPresentation } from './booking-presentation';
import { ScrollableChipRow } from './scrollable-chip-row';

// Sport metadata from the database.
// `id` is optional here because SportFilterToggle (this component) doesn't
// need it, but the booking flow uses it to resolve sport slug → UUID for the
// get_available_classes_for_date RPC (p_sport_id). It is always populated
// by extractSportsData() in use-booking-reducer.ts.
export interface SportData {
  id?: string;
  slug: string;
  name: string;
  /**
   * Secondary-language (Arabic) sport name set by the tenant (sports.name_alt).
   * Preferred over `name` when the UI renders in Arabic. Optional/NULL when
   * the tenant hasn't configured it.
   */
  name_alt?: string | null;
  icon: string | null;
  color: string | null;
  enableSplitPayment?: boolean;
  customerMaxSplitPayers?: number;
  adminMaxSplitPayers?: number;
}

interface SportFilterToggleProps {
  /** The card's heading; "See all" sits beside it once the sheet is offered. */
  heading?: ReactNode;
  value: string | null;
  onChange: (value: string) => void;
  availableSports: string[];
  sportsMetadata?: Record<string, SportData>;
  /**
   * When provided, prepends a full-width "All Classes" pill inside the same
   * bordered container as the sport pills. Used by the studio unified-class
   * filter so the "All" toggle and the sport row share one visual group.
   */
  allClassesPill?: {
    label: string;
    isActive: boolean;
    onSelect: () => void;
  };
  /**
   * Extra category pills (e.g. "Event") rendered on the same row as the sport
   * pills. Used by single-sport studios where the lone sport pill is redundant
   * with "All Classes": instead of stranding the sport's non-group_classes
   * categories in a separate box below, they sit beside "All Classes" as
   * top-level filters. The parent resolves the icon so this component stays
   * presentational.
   */
  categoryPills?: Array<{
    value: string;
    label: string;
    icon?: ReactNode;
    isActive: boolean;
    onSelect: () => void;
  }>;
}

// Fallback icons for known sports when database icon is not available
const fallbackIcons: Record<string, string> = {
  // Sports
  tennis: '/images/book/tennis-racket.svg',
  padel: '/images/book/padel-ball.svg',
  fitness: '/images/book/fitness.svg',
  pilates: '/images/book/fitness.svg',
  'table-tennis': '/images/book/table-tennis.svg',
  therapy: '/images/book/therapy.svg',
  // Clinic / medical services
  physiotherapy: '/images/book/activity.svg',
  dental: '/images/book/sparkles.svg',
  dermatology: '/images/book/hand.svg',
  pediatrics: '/images/book/baby.svg',
  orthopedics: '/images/book/bone.svg',
  ophthalmology: '/images/book/eye.svg',
  'general-medicine': '/images/book/stethoscope.svg',
  ent: '/images/book/ear.svg',
  cardiology: '/images/book/heart-pulse.svg',
  nutrition: '/images/book/apple.svg',
  psychology: '/images/book/brain.svg',
  chiropractic: '/images/book/activity.svg',
  'massage-therapy': '/images/book/hand.svg',
  acupuncture: '/images/book/clipboard-plus.svg',
  consultation: '/images/book/stethoscope.svg',
};

type SportIcon =
  | { kind: 'bundled'; src: string }
  | SportVisual;

// The sport's icon or uploaded image from Sports management wins, then the
// bundled SVGs, then a glyph matched from the slug's words.
function resolveSportIcon(
  sportSlug: string,
  metadata?: SportData,
): SportIcon | null {
  const chosen = resolveSportVisual('', metadata?.icon);
  // Bundled monochrome SVGs stay masked so they follow the tab's text colour.
  if (chosen?.kind === 'image' && chosen.src.startsWith('/images/book/')) {
    return { kind: 'bundled', src: chosen.src };
  }
  if (chosen) return chosen;

  const bundled = fallbackIcons[sportSlug.toLowerCase()];
  if (bundled) return { kind: 'bundled', src: bundled };

  return resolveSportVisual(sportSlug);
}

/** Uploaded images read better larger than the 24px glyphs. */
const SPORT_IMAGE_SIZE = 40;

function SportIconView({ icon, label }: { icon: SportIcon; label: string }) {
  if (icon.kind === 'bundled') {
    return <BookingChipIcon src={icon.src} alt={label} size={24} masked />;
  }

  return (
    <SportVisualView
      visual={icon}
      size={SPORT_IMAGE_SIZE}
      glyphSize={24}
    />
  );
}

/** Past this many tabs, "See all" beside the heading opens a sheet of every option. */
const SHEET_AFTER_TABS = 5;

const TAB_CLASS =
  'focus-visible:ring-ring flex min-w-max flex-col items-center gap-1.5 border-b-2 px-4 pt-2.5 pb-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset [&_svg]:size-6';

interface SportFilterItem {
  key: string;
  label: string;
  icon: ReactNode;
  hasImage?: boolean;
  isActive: boolean;
  onSelect: () => void;
}

export function SportFilterToggle({
  heading,
  value,
  onChange,
  availableSports,
  sportsMetadata = {},
  allClassesPill,
  categoryPills = [],
}: SportFilterToggleProps) {
  const { t, i18n } = useTranslation('landing');
  const presentation = useBookingPresentation();
  const stripRef = useRef<HTMLDivElement>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isArabic = i18n.language?.toLowerCase().startsWith('ar') ?? false;

  if (
    availableSports.length === 0 &&
    !allClassesPill &&
    categoryPills.length === 0
  ) {
    return null;
  }

  // When the "All Classes" pill is present, sport buttons act as a *narrowing*
  // filter — clicking one switches off "All" and scopes to that sport. When
  // "All" is active, dim the sport pills to communicate they're inactive.
  const sportsDimmed = allClassesPill?.isActive === true;

  const sportItems: SportFilterItem[] = availableSports.map((sportSlug) => {
    const metadata = sportsMetadata[sportSlug];
    const resolvedIcon = resolveSportIcon(sportSlug, metadata);
    const fallbackName =
      metadata?.name || sportSlug.charAt(0).toUpperCase() + sportSlug.slice(1);
    // Arabic UI: the tenant's configured name_alt wins over everything, so a
    // tenant naming a sport "صالة رياضية" shows that — not the English DB
    // name and not a generic hardcoded sports.<slug> string. When name_alt
    // is unset we fall through to the shared i18n key (e.g. "كرة قدم"), then
    // the English name. Non-Arabic UI keeps the prior i18n-key-first behavior.
    const arabicName = isArabic ? metadata?.name_alt?.trim() : undefined;
    const label =
      arabicName || t(`sports.${sportSlug}`, { defaultValue: fallbackName });

    return {
      key: sportSlug,
      label,
      icon: resolvedIcon ? (
        <SportIconView icon={resolvedIcon} label={label} />
      ) : null,
      hasImage: resolvedIcon?.kind === 'image',
      isActive: value === sportSlug && !sportsDimmed,
      // When the All Classes pill is active, every sport tap is a real
      // selection — even tapping the sport that happens to match the
      // carried-over state.sport must dispatch so the parent can exit "All"
      // mode. Without this, the leftmost sport (which usually matches the
      // default state.sport) becomes a no-op.
      onSelect: () => {
        if (sportsDimmed || value !== sportSlug) onChange(sportSlug);
      },
    };
  });

  const categoryItems: SportFilterItem[] = categoryPills.map((pill) => ({
    key: pill.value,
    label: pill.label,
    icon: pill.icon ?? null,
    isActive: pill.isActive,
    onSelect: () => {
      if (!pill.isActive) pill.onSelect();
    },
  }));

  const allItem: SportFilterItem | null = allClassesPill
    ? {
        key: 'all',
        label: allClassesPill.label,
        icon: (
          <LayoutGrid aria-hidden="true" className="size-6" strokeWidth={1.5} />
        ),
        isActive: allClassesPill.isActive,
        onSelect: () => {
          if (!allClassesPill.isActive) allClassesPill.onSelect();
        },
      }
    : null;

  const rowItems = [...sportItems, ...categoryItems];
  // Icons only when every option has a real one; a row of look-alike
  // placeholders (two "The …" sports) reads as broken. Tenants without
  // matches pick icons in Sports settings, or get plain underline tabs.
  // An uploaded picture always shows, leaving an empty slot for sports
  // without one, so a sibling with no icon never hides it.
  const showIcons =
    rowItems.every((item) => item.icon !== null) ||
    rowItems.some((item) => item.hasImage);
  const iconSlotClass = rowItems.some((item) => item.hasImage)
    ? 'flex size-10 items-center justify-center'
    : 'flex size-7 items-center justify-center';

  // One underline tab strip on web and native (design-system §6), icon over
  // label, that scrolls once the sports outgrow the row.
  const tabs = allItem ? [allItem, ...rowItems] : rowItems;
  const showSheet = tabs.length > SHEET_AFTER_TABS;

  const pickFromSheet = (item: SportFilterItem) => {
    item.onSelect();
    setSheetOpen(false);
    // Bring the picked tab into view so the strip shows what's selected.
    requestAnimationFrame(() => {
      stripRef.current
        ?.querySelector(`[data-sport-tab="${CSS.escape(item.key)}"]`)
        ?.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
            .matches
            ? 'instant'
            : 'smooth',
        });
    });
  };

  return (
    <div ref={stripRef} data-test="sport-filter">
      {(heading || showSheet) && (
        <div className="mb-2 flex items-center justify-between gap-3">
          {heading ?? <span />}
          {showSheet && (
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              aria-haspopup="dialog"
              data-booking-link
              className="text-foreground hover:text-foreground/75 focus-visible:ring-ring -me-1 inline-flex min-h-11 shrink-0 items-center gap-0.5 rounded-md px-1 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
              data-test="sport-filter-see-all"
            >
              {t('bookings:sportsSeeAll', { count: rowItems.length })}
              <ChevronRight
                aria-hidden="true"
                className="size-4 rtl:rotate-180"
              />
            </button>
          )}
        </div>
      )}

      <div className="border-b">
        <ScrollableChipRow
          backLabel={t('bookings:sportsScrollBack')}
          forwardLabel={t('bookings:sportsScrollForward')}
          testId="sport-filter"
          contentClassName="min-w-full gap-1 p-0"
          arrows="overlay"
        >
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={item.isActive}
              onClick={item.onSelect}
              data-sport-tab={item.key}
              data-booking-tab
              className={cn(
                TAB_CLASS,
                'flex-1',
                item.isActive
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground border-transparent',
              )}
            >
              {showIcons && (
                <span className={iconSlotClass} aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </ScrollableChipRow>
      </div>

      {showSheet && (
        <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
          <DrawerContent
            {...presentation}
            className={cn(
              'md:!right-[calc(50%-21rem)] md:!left-[calc(50%-21rem)]',
              presentation.className,
            )}
          >
            <div className="relative flex max-h-[85vh] flex-col">
              <DrawerClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute end-3 top-3 z-10 h-9 w-9 rounded-full"
                  aria-label={t('bookings:sportsSheetClose')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
              <div className="px-6 pe-14 pt-4 pb-3">
                <DrawerTitle className="text-xl font-semibold">
                  {t('bookings:sportsSheetTitle')}
                </DrawerTitle>
              </div>
              <div
                className="grid grid-cols-3 gap-2 overflow-y-auto px-6 pb-6 sm:grid-cols-4"
                data-test="sport-filter-sheet"
              >
                {tabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    aria-pressed={item.isActive}
                    onClick={() => pickFromSheet(item)}
                    className={cn(
                      'focus-visible:ring-ring flex min-h-20 flex-col items-center justify-center gap-2 rounded-lg border px-2 py-3 text-center text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none [&_svg]:size-6',
                      item.isActive
                        ? 'border-primary text-foreground ring-primary ring-1'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    )}
                  >
                    {showIcons && (
                      <span className={iconSlotClass} aria-hidden="true">
                        {item.icon}
                      </span>
                    )}
                    <span className="leading-tight">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
